import { useState, useMemo, useEffect } from "react";
import { type TestProject, TestCard } from "@/features/tests/TestCard";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { getCompletedTests } from "@/lib/completedTests";

export type { TestProject };

interface TestListProps {
  projects: TestProject[];
  onPreview: (project: TestProject) => void;
  initialVisibleCount?: number;
  isLocked?: boolean;
  onLockedClick?: (project: TestProject) => void;
}

export function TestList({
  projects,
  onPreview,
  initialVisibleCount,
  isLocked = false,
  onLockedClick,
}: TestListProps) {
  const [completedTests, setCompletedTests] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [visibleCount, setVisibleCount] = useState(() =>
    typeof initialVisibleCount === "number" && initialVisibleCount > 0
      ? initialVisibleCount
      : projects.length,
  );

  useEffect(() => {
    let cancelled = false;
    getCompletedTests().then((tests) => {
      if (!cancelled) {
        setCompletedTests(tests);
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const handleStorage = () => {
      getCompletedTests().then(setCompletedTests);
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  const sortedProjects = useMemo(() => {
    return [...projects].sort((a, b) => {
      const aDone = completedTests.includes(a.id);
      const bDone = completedTests.includes(b.id);
      if (aDone === bDone) return 0;
      return aDone ? 1 : -1;
    });
  }, [projects, completedTests]);

  const hasMore = sortedProjects.length > visibleCount;
  const visibleProjects = sortedProjects.slice(0, visibleCount);

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {visibleProjects.map((project) => (
          <TestCard
            key={project.id}
            project={project}
            onPreview={onPreview}
            isLocked={isLocked}
            onLockedClick={onLockedClick}
            isCompleted={completedTests.includes(project.id)}
          />
        ))}
      </div>

      {hasMore && (
        <div className="flex justify-center">
          <Button
            variant="outline"
            className="min-h-[44px]"
            onClick={() => setVisibleCount((c) => c + 6)}
          >
            See More
          </Button>
        </div>
      )}
    </div>
  );
}
